import React, { useState, ReactNode } from 'react'
import {
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
  SxProps,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

interface CollapsibleSectionProps {
  title: string
  count?: number
  icon?: React.ComponentType<{ sx?: SxProps }> | null
  defaultExpanded?: boolean
  children: ReactNode
  actions?: ReactNode
  onAdd?: () => void
  onDeleteSelected?: () => void
  deleteDisabled?: boolean
  deleteCount?: number
}

export default function CollapsibleSection({
  title,
  count,
  icon,
  defaultExpanded = false,
  children,
  actions,
  onAdd,
  onDeleteSelected,
  deleteDisabled = true,
  deleteCount = 0,
}: CollapsibleSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <Box
      sx={{
        mb: 4,
        width: '100%',
        backgroundColor: 'common.white',
        borderRadius: '8px',
        p: 2,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: expanded ? 2 : 0, cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <Box
            sx={{
              backgroundColor: expanded ? 'primary.main' : 'grey.100',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '50%',
            }}
          >
            {icon
              ? React.createElement(icon, {
                  sx: {
                    fontSize: 20,
                    color: expanded ? 'common.white' : 'grey.500',
                  },
                })
              : null}
          </Box>
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {count != null && (
            <Chip
              label={count}
              size='small'
              sx={{
                fontWeight: 600,
                backgroundColor: 'grey.100',
                color: 'grey.500',
                height: 24,
                minWidth: 32,
              }}
            />
          )}
        </Stack>
        <Stack direction='row' alignItems='center' spacing={0.5}>
          {actions && expanded && (
            <Box onClick={(e) => e.stopPropagation()}>{actions}</Box>
          )}
          {onAdd && expanded && (
            <Tooltip title='Add'>
              <span>
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation()
                    onAdd()
                  }}
                  aria-label='Add'
                >
                  <AddIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onDeleteSelected && expanded && (
            <Tooltip
              title={
                deleteCount > 0
                  ? `Remove selected (${deleteCount})`
                  : 'Remove selected'
              }
            >
              <span>
                <IconButton
                  size='small'
                  color='error'
                  onClick={(e) => {
                    e.stopPropagation()
                    onDeleteSelected()
                  }}
                  disabled={deleteDisabled}
                  aria-label='Remove selected'
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton
            size='small'
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Collapse in={expanded}>{children}</Collapse>
    </Box>
  )
}

CollapsibleSection.defaultProps = {
  count: undefined,
  icon: undefined,
  defaultExpanded: false,
  actions: undefined,
  onAdd: undefined,
  onDeleteSelected: undefined,
  deleteDisabled: true,
  deleteCount: 0,
}
